import React, { useCallback, useEffect, useState } from "react";

import { Button, Col, Form, Row } from "react-bootstrap";

import { Controller, useForm } from "react-hook-form";
import { useTypedParams } from "react-router-typesafe-routes/dom";
import { ROUTES } from "src/routes";

import { BankDetailsFragment, BankType, ParticipantTypes, useUpdateBankDetailsMutation } from "@api/graphql";
import { PenWriteEditIcon } from "@assets/icons";
import { IconLink } from "@components/IconLink";
import { ToastMessage } from "@components/ToastMessage";
import { CustomAccordion } from "@components/accordion/CustomAccordion";
import { FieldFormatter } from "@components/detail_drawer/FieldFormatter";
import { ProtectedComponent } from "@components/permission_control/ProtectedComponent";
import { DetailCell } from "@components/resale_submission/DetailCell";
import { useAuthenticationContext } from "@context/authentication";
import { UserDetailsInput } from "@pages/register/steps/RegisterFormTypes";
import { DomesticBankDetails } from "@pages/register/steps/UserDetails/DomesticBankDetails";
import { Role } from "@roles";

import { bankDetailsErrorResolver } from "./bankDetailsErrorResolver";

import styles from "./BankDetailsAccordion.module.scss";

interface BankDetailsProps {
  bankDetails: BankDetailsFragment;
  onBankDetailsTypeChange: (type: BankType) => void;
  isEditbankDetails: boolean;
  setIsEditbankDetails: (type: boolean) => void;
  onUpdate: () => void;
  participantTypes: (ParticipantTypes | undefined)[];
}

const BANK_DETAILS_FORMATTERS: FieldFormatter<BankDetailsFragment>[] = [
  { title: "Account name", format: (bankDetails: BankDetailsFragment) => bankDetails.account_name || "-" },
  { title: "Account number", format: (bankDetails: BankDetailsFragment) => bankDetails.account_number || "-" },
  { title: "BSB", format: (bankDetails: BankDetailsFragment) => bankDetails.bsb || "-" },
];

export const DomesticBankDetailsAccordion: React.FC<BankDetailsProps> = ({
  bankDetails,
  onBankDetailsTypeChange,
  isEditbankDetails,
  setIsEditbankDetails,
  onUpdate,
  participantTypes,
}) => {
  const { auth } = useAuthenticationContext();

  const [bankInfo, setBankInfo] = useState(bankDetails);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState("");
  const [showToast, setShowToast] = useState(false);
  const [updateBankDetailsMutation] = useUpdateBankDetailsMutation();
  const { participantId } = useTypedParams(ROUTES.Profile.AccountDetails);
  const {
    control,
    formState: { errors },
    getValues,
    handleSubmit,
    reset,
  } = useForm<UserDetailsInput>({
    mode: "onChange",
    reValidateMode: "onChange",
    resolver: (data) => {
      return bankDetailsErrorResolver(
        data,
        BankType.DOMESTIC,
        participantTypes?.filter((type): type is ParticipantTypes => type !== undefined).map((type) => type.toString()),
      );
    },
  });

  useEffect(() => {
    reset({ bank_details: { ...bankInfo, bank_type: BankType.DOMESTIC } });
  }, [bankInfo, reset]);

  const handleEditToggle = () => {
    setIsEditbankDetails(!isEditbankDetails);
  };

  const submitHandler = useCallback(async () => {
    const formData = getValues();
    try {
      const response = await updateBankDetailsMutation({
        variables: {
          input: {
            account_name: formData.bank_details.account_name,
            account_number: formData.bank_details.account_number,
            bsb: formData.bank_details.bsb,
            bank_type: BankType.DOMESTIC,
            participant_id: participantId ?? -1,
          },
        },
      });
      if (response.data?.updateBankDetails) {
        setIsEditbankDetails(false);
        setBankInfo(response.data.updateBankDetails);
        onUpdate();
        setToastMessage("Bank details updated successfully!");
      } else {
        setErrorMessage("Failed to update bank details");
        setToastMessage("Failed to update bank details");
      }
    } catch (error) {
      setErrorMessage(`An error occurred while updating bank details: ${error}`);
      setToastMessage("An error occurred while updating bank details");
    } finally {
      setShowToast(true);
    }
  }, [getValues, onUpdate, participantId, setIsEditbankDetails, updateBankDetailsMutation]);

  return (
    <>
      <ToastMessage message={toastMessage} show={showToast} setShow={setShowToast} type="info" />
      <CustomAccordion
        accordionKey={"DomesticBankDetails"}
        contrastOpenStyle={false}
        header={<h5 className="mb-0">Domestic bank details</h5>}
        defaultActiveKey={"DomesticBankDetails"}
        customHeaderStyle="py-4"
        showBottomLine
      >
        <div onClick={(e) => e.stopPropagation()}>
          {isEditbankDetails ? (
            <>
              <Row>
                <Col md={12} sm={12}>
                  <Form.Group>
                    <Form.Label>Is your bank domestic or overseas?</Form.Label>
                    <Row>
                      <Col>
                        <Controller
                          name="bank_details.bank_type"
                          control={control}
                          render={({ field }) => (
                            <>
                              <Form.Check
                                className={styles["form-check"]}
                                type="radio"
                                label="Domestic"
                                value="DOMESTIC"
                                checked={field.value === "DOMESTIC"}
                                onChange={() => {
                                  field.onChange("DOMESTIC");
                                  onBankDetailsTypeChange(BankType.DOMESTIC);
                                }}
                              />
                            </>
                          )}
                        />
                      </Col>
                      <Col>
                        <Controller
                          name="bank_details.bank_type"
                          control={control}
                          render={({ field }) => (
                            <>
                              <Form.Check
                                className={styles["form-check"]}
                                type="radio"
                                label="Overseas"
                                value="OVERSEAS"
                                checked={field.value === "OVERSEAS"}
                                onChange={() => {
                                  field.onChange("OVERSEAS");
                                  onBankDetailsTypeChange(BankType.OVERSEAS);
                                }}
                              />
                            </>
                          )}
                        />
                      </Col>
                    </Row>
                  </Form.Group>
                </Col>
              </Row>
              <DomesticBankDetails control={control} errors={errors} />
              <Row className="mt-3">
                {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}
                <Col className="d-flex justify-content-end">
                  <Button variant="secondary" onClick={() => setIsEditbankDetails(false)} className="me-2">
                    Cancel
                  </Button>
                  <Button variant="primary" onClick={handleSubmit(submitHandler)}>
                    Save
                  </Button>
                </Col>
              </Row>
            </>
          ) : (
            <Row>
              <Col md={11}>
                <Row className="gap-4">
                  {BANK_DETAILS_FORMATTERS.map(({ title, format }) => (
                    <Col md={5} className="d-flex flex-column" key={title}>
                      <DetailCell title={title} value={format(bankInfo)} />
                    </Col>
                  ))}
                </Row>
              </Col>
              <Col md={1} className="d-flex justify-content-end">
                {!isEditbankDetails && (
                  <ProtectedComponent requiredGroups={[Role.MemberServices, Role.RoyaltyRecipient]} auth={auth}>
                    <IconLink to="#" src={PenWriteEditIcon} text="Edit" className="me-2" onClick={handleEditToggle} />
                  </ProtectedComponent>
                )}
              </Col>
            </Row>
          )}
        </div>
      </CustomAccordion>
    </>
  );
};
